import React from 'react';
import { motion } from 'motion/react';
import { LucideIcon } from 'lucide-react';

import { MockTag } from './MockTag';

interface EnergyStatsCardProps {
  title: string;
  value: string;
  unit: string;
  change: string;
  changeType: 'positive' | 'negative';
  icon: LucideIcon | React.ComponentType<any>;
  gradient?: boolean;
  delay?: number;
  isMock?: boolean;
}

const EnergyStatsCard: React.FC<EnergyStatsCardProps> = ({
  title,
  value,
  unit,
  change,
  changeType,
  icon: Icon,
  gradient = false,
  delay = 0,
  isMock = false,
}) => {
  const cardClasses = gradient
    ? 'rounded-3xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 p-6 text-white shadow-xl shadow-emerald-500/25'
    : 'rounded-3xl border border-gray-100/50 bg-white/80 backdrop-blur-sm p-6 shadow-xl shadow-gray-900/5';

  const iconClasses = gradient ? 'rounded-2xl bg-white/20 backdrop-blur-sm p-3' : 'rounded-2xl bg-gray-50/80 p-3';

  const changeColor =
    changeType === 'positive'
      ? gradient
        ? 'text-white/90'
        : 'text-emerald-600'
      : gradient
        ? 'text-white/90'
        : 'text-red-500';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="group"
    >
      <div className={`${cardClasses} transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className={`text-sm font-medium ${gradient ? 'text-white/80' : 'text-gray-600'}`}>{title}</p>
              {isMock && (
                <MockTag className={gradient ? '!bg-white/20 !border-white/30 !text-white' : ''} />
              )}
            </div>
            <div className="mb-1 flex items-baseline gap-2">
              <h3 className={`text-3xl font-bold ${gradient ? 'text-white' : 'text-gray-900'}`}>{value}</h3>
              <span className={`text-sm ${gradient ? 'text-white/70' : 'text-gray-500'}`}>{unit}</span>
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium ${changeColor}`}>
              <span>{change}</span>
            </div>
          </div>
          <div className={iconClasses}>
            <Icon className={`h-6 w-6 ${gradient ? 'text-white' : 'text-gray-700'}`} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EnergyStatsCard;
